/* eslint-disable no-useless-escape */
/* eslint-disable no-prototype-builtins, camelcase */
import { useEffect, useState, useMemo, useContext } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Table, FileTypeCell, FileSanitizationStats, NotificationContext } from '@mdc/ui';
import { metadefenderService, Authenticator, TranslationFix } from '@mdc/services';
import { FILE_EXTENSION_ICONS, NOTIFICATION_TYPES } from '@mdc/constants';
import classnames from 'classnames';
import GartnerLogo from '../../../assets/images/Gartner_logo.svg';
import { OutboundLink } from 'gatsby-plugin-google-analytics';

import './Sanitization.scss';

const Sanitization = ({ pageName }) => {
    const { t, ready } = useTranslation();
    const [reportData, setReportData] = useState([]);
    const [columnsData, setColumnsData] = useState([
        {
            Header: t('File type'),
            accessor: 'file_type_extension',
            component: FileTypeCell,
            filter: []
        },
        {
            Header: t('File name'),
            accessor: 'display_name'
        },
        {
            Header: t('Threat name'),
            accessor: 'malware_name',
        },
        {
            Header: t('Before sanitization'),
            accessor: 'original_file',
            component: FileSanitizationStats
        },
        {
            Header: t('After sanitization'),
            accessor: 'sanitized_file',
            component: FileSanitizationStats
        }
    ]);
    const { notify } = useContext(NotificationContext);

    useEffect(() => {
        (async () => {
            try {
                await Authenticator.init();
                const [sanitizationReport] = metadefenderService.report.getSanitization();
                const data = await sanitizationReport;
                setReportData(data?.data?.data ?? []);
            } catch (e) {
                notify({
                    message: t('Network error occurred, and your request could not be completed. Please refresh the page'),
                    type: NOTIFICATION_TYPES.CRITICAL
                });
            }
        })();
    }, []);

    if (!ready) {
        return null;
    }

    const tableData = useMemo(() => {
        return reportData
            .filter(({ data }) => {
                // Filter only the items that had no threats found after sanitization
                // and the items that have the total_detected and total_avs properties for both
                // the sanitized and original files
                return (data.original_file && data.sanitized_file &&
                    data.original_file.hasOwnProperty('total_avs') && data.original_file.hasOwnProperty('total_detected') &&
                    data.sanitized_file.hasOwnProperty('total_avs') && data.sanitized_file.hasOwnProperty('total_detected') &&
                    data.sanitized_file.total_detected === 0);
            })
            .map(({ data: { file_type_extension, file_type_description, display_name, malware_name, original_file, sanitized_file } }) => {
                return {
                    file_type_extension,
                    file_type_description,
                    display_name,
                    malware_name,
                    original_file: { type: 'before_sanitization', data: original_file },
                    sanitized_file: { type: 'after_sanitization', data: sanitized_file }
                };
            });
    }, [reportData]);

    const tableFilters = useMemo(() => {
        const filters = {};

        const getIconForExt = (ext) => {
            const map = FILE_EXTENSION_ICONS;

            return map[ext] || map['gen'];
        };


        tableData.forEach(({ file_type_extension, file_type_description }) => {
            if (file_type_extension && file_type_description && !filters.hasOwnProperty(file_type_extension)) {
                filters[file_type_extension] = file_type_description;
            }
        });

        return Object.keys(filters).map((key) => {
            const iconClassNames = classnames('icon', getIconForExt(key));
            const icon = (<i className={iconClassNames}/>);
            return { value: key, label: filters[key], icon };
        });
    }, [tableData]);



    useEffect(() => {
        const data = columnsData;
        data[0].filter = tableFilters;
        setColumnsData([...data]);
    }, [tableFilters]);

    const tableFooterContent = null;

    const TableComponent = useMemo(() => {
        return <Table columns={columnsData} data={tableData} rowsPerPage={25} useSorting={false} footerInfoBox={tableFooterContent}/>;
    }, [reportData, columnsData, tableData]);

    return (
        <div className={pageName}>
            <div className="sanitizationReport">
                <Row>
                    <Col>
                        <h1>{t('Deep CDR Report')}</h1>
                    </Col>
                </Row>
                <Row>
                    <Col sm={12} md={6}>
                        <p className="intro">{t('Data sanitization, also known as Content Disarm and Reconstruction (CDR), is an advanced threat prevention technology that does not rely on detection. Instead, it assumes all files are malicious and sanitizes and rebuilds each file ensuring full usability with safe content')}</p>
                    </Col>
                    <Col sm={12} md={6}>
                        <div className="multiscan">
                            <i className="icon icon-multiscan"/>
                            <div className="content">
                                <span className="title">{t('metascan')}</span>
                                <span>{t('Multiscanning')}</span>
                            </div>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {TableComponent}
                    </Col>
                </Row>
                <Row>
                    <Col className="moreInfo" sm={12} md={6}>
                        <TranslationFix
                            message={t('The technology is highly effective for preventing known and unknown threats, including zero-day targeted attacks and threats that are equipped with malware evasion technology such as Fully Undetectable (FUD)  , VMware detection, obfuscation and many others. <a href=\"https://www.opswat.com/technologies/data-sanitization\" rel=\"noopener noreferrer\" target=\"_blank\">Read more ...</a>')}
                            type='p'
                        />
                        <p>{t('This report contains infected files that have been sanitized by OPSWAT MetaDefender Cloud. File samples are uploaded by OPSWAT customers and community members. View a sample below to see how our technology strips malicious threats from common document types while preserving file usability')}</p>
                        <OutboundLink href="https://onlinehelp.opswat.com/cdr/" className="learnMore btn btn-primary">{t('Learn more about Deep CDR')}</OutboundLink>
                    </Col>
                    <Col>
                        <div className="quote" sm={12} md={6}>
                            <GartnerLogo className="quoteLogo"/>
                            <p>{t('As malware sandbox evasion techniques improve, the use of CDR at the email gateway as a supplement or alternative to sandboxing will increase')}</p>
                            <div className="quoteFooter">
                                <span>{t('Gartner')}</span>
                                {t('Fighting Phishing: Optimize Your Defense')}
                            </div>
                        </div>

                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default Sanitization;

Sanitization.propTypes = {
    pageName: PropTypes.string.isRequired
};
